import Table from 'react-bootstrap/Table';
import { useTable, useSortBy, useGroupBy } from 'react-table';
import { useEffect, useMemo, useState } from 'react';
import InfoOverlay from '../../../components/InfoOverlay';
import RaceClassDropdown from '../../../components/RaceClassDropdown';
import RegistrationBtnGroup from './RegistrationBtnGroup';
import usePaginatedTable from '../../../hooks/UsePaginatedTable';
import PaginationSection from '../../../components/pagination/PaginationSection';
import CheckboxSelectAlllRegistrations from './CheckboxSelectAllRegistrations';

function RegistrationsTable({
  registrations,
  status,
  setRegistrations,
  rowsPerPage,
  batchSelectedRegistrations,
  setBatchSelectedRegistrations,
}) {
  // Pagination
  const [page, setPage] = useState(1);
  const { slice, range } = usePaginatedTable(registrations, page, rowsPerPage);

  const data = useMemo(() => slice, [registrations, slice]);

  const [allOnCurrentPageSelected, setAllOnCurrentPageSelected] =
    useState(false);

  useEffect(() => {
    const registrationsRacingNumbers = batchSelectedRegistrations.map(
      (r) => r.racingNumber
    );
    const sliceRacingNumbers = slice.map((s) => s.racingNumber);

    const allElementsOnPageSelected = sliceRacingNumbers.every((elem) =>
      registrationsRacingNumbers.includes(elem)
    );

    setAllOnCurrentPageSelected(allElementsOnPageSelected);
  }, [slice, batchSelectedRegistrations]);

  const columns = useMemo(
    () => [
      {
        id: 'cb-column',
        accessor: '',
        Header: () => (
          <>
            {(status === 'Kommande' || status === 'Pågående') && (
              <CheckboxSelectAlllRegistrations
                batchSelectedRegistrations={batchSelectedRegistrations}
                registrations={registrations}
                setBatchSelectedRegistrations={setBatchSelectedRegistrations}
                slice={slice}
              />
            )}
          </>
        ),
        className: 'p-3 ps-2',
        style: { width: '100px' },
        Cell: (props) => (
          <>
            <input
              type='checkbox'
              disabled={status !== 'Kommande' && status !== 'Pågående'}
              checked={batchSelectedRegistrations.some(
                (r) =>
                  parseInt(r.racingNumber) ===
                  parseInt(props.row.original.racingNumber)
              )}
              onChange={() => {
                const registration = props.row.original;
                let newArr = [...batchSelectedRegistrations];

                if (
                  newArr.some(
                    (r) => r.racingNumber === registration.racingNumber
                  )
                ) {
                  newArr = newArr.filter(
                    (r) => r.racingNumber !== registration.racingNumber
                  );
                } else {
                  newArr = [...newArr, registration];
                }
                setBatchSelectedRegistrations(newArr);
              }}
            />
          </>
        ),
        disableSortBy: true,
      },
      {
        Header: 'Tävlande',
        accessor: 'racingNumber',
        className: 'p-3 ps-2',
        style: { width: '25%' },
        Cell: (props) => (
          <div className='d-flex align-items-center'>
            <span className='bg-secondary-black text-white p-1 me-2'>
              {props.row.original.racingNumber}
            </span>
            <InfoOverlay
              name={props.row.original.name}
              info={props.row.original.participantDetails}
              unRegistered={props.row.original.unRegistered}
              participantInfo={true}
            />
          </div>
        ),
      },
      {
        Header: 'Klubb',
        accessor: 'clubAffiliation',
        className: 'p-3 ps-2',
        style: { width: '20%' },
        Cell: ({ cell: { value } }) => (
          <div className='d-flex gap-1'>{value ? value : 'Ej angett'}</div>
        ),
      },
      {
        Header: 'Tävlingsklass',
        accessor: 'raceClass',
        className: 'p-3 ps-2',
        style: { width: '10%' },
        Cell: (props) => (
          <div className='d-flex gap-1'>
            {props.row.original.participantId ? (
              <RaceClassDropdown
                value={props.row.original.raceClass}
                status={status}
                setRegistrations={setRegistrations}
                participantId={props.row.original.participantId}
                disabled={props.row.original.unRegistered}
              />
            ) : (
              <RaceClassDropdown
                value={props.row.original.raceClass}
                status={status}
                setRegistrations={setRegistrations}
                email={props.row.original.participantDetails?.email}
                disabled={props.row.original.unRegistered}
              />
            )}
          </div>
        ),
      },
      {
        Header: 'Anmälare',
        accessor: 'supporter',
        className: 'p-3 ps-2',
        style: { width: '16%' },
        Cell: ({ cell: { value } }) => (
          <div className='d-flex gap-1'>
            {value ? (
              <InfoOverlay
                name={value.name}
                info={value}
                supporterInfo={true}
              />
            ) : (
              'Ej angett'
            )}
          </div>
        ),
      },
      {
        Header: 'Övrig info',
        accessor: 'miscellaneous',
        className: 'p-3 ps-2',
        style: { width: '24%' },
        Cell: ({ cell: { value } }) => (
          <div className='d-flex gap-1'>
            {value ? <InfoOverlay info={value} miscInfo={true} /> : 'Ej angett'}
          </div>
        ),
      },
      {
        Header: '',
        accessor: 'participantId',
        className: 'p-3',
        style: { width: '5%' },
        Cell: (props) => (
          <div className='d-flex'>
            <RegistrationBtnGroup
              status={status}
              participantId={props.row.original.participantId}
              email={props.row.original.participantDetails?.email}
              unRegistered={props.row.original.unRegistered}
              inStartlist={props.row.original.inStartlist}
              setRegistrations={setRegistrations}
            />
          </div>
        ),
      },
    ],
    [
      status,
      registrations,
      batchSelectedRegistrations,
      slice,
      allOnCurrentPageSelected,
    ]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
      },
      useGroupBy,
      useSortBy
    );

  return (
    <div className='d-flex flex-column w-100'>
      <Table
        {...getTableProps()}
        style={{
          borderCollapse: 'separate',
          borderSpacing: '0 0.5rem',
          width: '100%',
        }}
        className='table-borderless'
        hover
      >
        <thead className='bg-divider'>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr
                {...row.getRowProps()}
                className='bg-white'
                style={{ borderStyle: 'none' }}
              >
                {row.cells.map((cell) => {
                  return (
                    <td
                      {...cell.getCellProps()}
                      className={cell.column.className}
                      style={cell.column.style}
                    >
                      {cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
      <PaginationSection
        range={range}
        slice={slice}
        setPage={setPage}
        page={page}
      />
    </div>
  );
}

export default RegistrationsTable;
